import { useBlockProps, InnerBlocks } from '@wordpress/block-editor';
import { useEffect } from '@wordpress/element';
import { useSelect, dispatch } from '@wordpress/data';
import SidePanel from './sidePanel.js';
import { t } from "drupal";
import { store as blockEditorStore } from "@wordpress/block-editor";
import { useDispatch } from '@wordpress/data';
import { store as noticesStore } from '@wordpress/notices';
import { useRef } from '@wordpress/element';
import CustomRepater from "./customRepeater.js";



const Edit = ({ attributes, setAttributes, clientId }) => {
    const { createNotice } = useDispatch(noticesStore);
    const blockProps = useBlockProps();
    const { numCols, firstBlockType, firstColor, firstColorClass } = attributes;
    const { updateBlockAttributes } = useDispatch(blockEditorStore);

    const previousFirstColorRef = useRef();

    const innerBlocks = useSelect(
        (select) => select('core/block-editor').getBlocks(clientId),
        [clientId]
    );


    const AVAILABLE_BLOCKS = [
        'deohs-gutenberg-blocks/deohs-card-vertical01',
        'deohs-gutenberg-blocks/deohs-card-horizontal',
        'deohs-gutenberg-blocks/deohs-card-square',
        'deohs-gutenberg-blocks/deohs-card-no-image',
        'deohs-gutenberg-blocks/deohs-image-backed-card'
    ];

    const BLOCK_LABELS = {
        "deohs-gutenberg-blocks/deohs-card-horizontal": "Horizontal card",
        "deohs-gutenberg-blocks/deohs-card-vertical01": "Vertical card",
        "deohs-gutenberg-blocks/deohs-card-square": "Square card",
        "deohs-gutenberg-blocks/deohs-card-no-image": "No-image card",
        "deohs-gutenberg-blocks/deohs-image-backed-card": "Image-backed card",
    }

    // console.log("edit: ", { numCols, firstBlockType, firstColor, firstColorClass });


    const invalidBlocks = useSelect((select) => {
        const { getBlocks } = select(blockEditorStore);
        const innerBlocks = getBlocks(clientId);

        return innerBlocks
            .filter((block) => {
                const { name, attributes } = block;

                switch (name) {

                    case "deohs-gutenberg-blocks/deohs-card-horizontal":
                    case "deohs-gutenberg-blocks/deohs-card-vertical":
                    case "deohs-gutenberg-blocks/deohs-card-square":
                        return (
                            attributes.titleError ||
                            attributes.imageError ||
                            attributes.ctaError ||
                            attributes.ctaUrlError
                        );
                    case "deohs-gutenberg-blocks/deohs-card-no-image":
                        return (
                            attributes.titleError || attributes.ctaError || attributes.ctaUrlError
                        );
                    default:
                        return false;
                }
            })
            .map((block) => BLOCK_LABELS[block.name] || block.name); // Map to readable labels
    }, [clientId]);


    // Set firstBlockType when the first block is added
    useEffect(() => {
        if (!firstBlockType && innerBlocks.length > 0) {
            setAttributes({ firstBlockType: innerBlocks[0].name });
        }
    }, [innerBlocks, firstBlockType, setAttributes]);


    // Set the first background color of the card group
    useEffect(() => {
        if (!firstColor && innerBlocks.length > 0) {
            const firstBlock = innerBlocks[0];
            const bgColor = firstBlock?.attributes?.backgroundColor;

            // If the first block has a defined backgroundColor, set it as the group's firstColor
            if (bgColor && bgColor !== '') {
                setAttributes({ firstColor: bgColor });
            }
        }
    }, [innerBlocks, firstColor, setAttributes]);


    // Apply Bootstrap row classes based on numCols
    useEffect(() => {
        const gridContainer = document.querySelector(`[data-block="${clientId}"] .row`);
        if (gridContainer) {
            gridContainer.className = `row row-cols-1 row-cols-sm-2 row-cols-md-${numCols} g-3`;
        }
    }, [numCols, clientId]);

    const updateNumCols = (value) => {
        setAttributes({ numCols: value });
    };

    const updateFirstBlockType = (blockType) => {
        setAttributes({ firstBlockType: blockType });

        // Replace existing blocks with the selected type
        const newBlocks = Array(numCols).fill().map(() =>
            wp.blocks.createBlock(blockType)
        );
        dispatch('core/block-editor').replaceInnerBlocks(clientId, newBlocks);
    };

    // Update the background color of inner blocks when firstColor changes
    useEffect(() => {
        if (firstColor) {
            innerBlocks.forEach((block) => {
                const currentColor = block.attributes?.backgroundColor;

                const wasSetByGroup =
                    !currentColor ||
                    currentColor === '' ||
                    currentColor === previousFirstColorRef.current;

                if (wasSetByGroup) {
                    updateBlockAttributes(block.clientId, {
                        backgroundColor: firstColor,
                        backgroundColorClass: firstColorClass,
                    });
                }
            });

            // Update the previousFirstColor after applying
            previousFirstColorRef.current = firstColor;
        }
    }, [firstColor, innerBlocks, firstColorClass]);



    return (
        <>
            {invalidBlocks.length > 0 && (
                <div className="parent-block-container-error">
                    <p>The following blocks have errors and must be fixed before saving:</p>
                    <ul>
                        {invalidBlocks.map((blockName, index) => (
                            <li key={index}>{blockName}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Side Panel */}
            <SidePanel
                numCols={numCols}
                updateNumCols={updateNumCols}
                firstBlockType={firstBlockType}
                updateFirstBlockType={updateFirstBlockType}
                firstColor={firstColor}
                setAttributes={setAttributes}
            />

            {/* Block Layout */}
            <div {...blockProps}>
                <div className={`row row-cols-1 row-cols-md-${numCols} g-3`}>
                    <InnerBlocks
                        allowedBlocks={firstBlockType ? [firstBlockType] : AVAILABLE_BLOCKS}
                        template={[]}
                        templateLock={false}
                        // Turn off the default one
                        renderAppender={false}
                    />
                </div>
                <CustomRepater />
            </div>
        </>
    );
};

export default Edit;
